import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PageShell from '../components/PageShell.jsx';
import LeadForm from '../components/LeadForm.jsx';
import Seo from '../components/Seo.jsx';
import JsonLd, { buildFaqPageSchema } from '../components/JsonLd.jsx';
import FaqAccordion from '../components/FaqAccordion.jsx';
import { getHome, getSeo, getSite } from '../content/index.js';
import { locationLabel } from '../utils/format.js';
import { scrollToUniversities } from '../utils/scroll.js';
import UniversityLogo from '../components/UniversityLogo.jsx';
import { catalogLevels } from '../utils/courseLevels.js';
import CampusImage, { CampusCreditText, getCampusImage } from '../components/CampusImage.jsx';
import CounsellorSection from '../components/CounsellorSection.jsx';
import { useFindCourse } from '../components/FindCourseContext.js';

export default function HomePage() {
  const home = getHome();
  const seo = getSeo();
  const site = getSite();
  const location = useLocation();
  const { openFindCourse } = useFindCourse();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [selectedLevel, setSelectedLevel] = useState(searchParams.get('level') || '');
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const searchBoxRef = useRef(null);

  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
    setSelectedLevel(searchParams.get('level') || '');
  }, [searchParams]);

  // Debounced autocomplete while the visitor types.
  useEffect(() => {
    const text = searchInput.trim();
    if (!suggestOpen || text.length < 1) {
      setSuggestions([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch(`/api/search/suggest?q=${encodeURIComponent(text)}`, {
          signal: controller.signal
        });
        if (!res.ok) return;
        const payload = await res.json();
        const list = payload.data?.suggestions || [];
        const didYouMean = payload.data?.didYouMean;
        setSuggestions(
          list.length || !didYouMean
            ? list
            : [{ type: 'didYouMean', label: `Did you mean "${didYouMean}"?`, query: didYouMean }]
        );
        setActiveSuggestion(-1);
      } catch {
        // aborted or offline: keep the previous list
      }
    }, 200);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [searchInput, suggestOpen]);

  useEffect(() => {
    function onPointerDown(event) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setSuggestOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  useEffect(() => {
    if (location.hash !== '#universities') {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const scrollWhenReady = () => {
      if (cancelled) return;
      if (scrollToUniversities() || attempts >= 40) return;
      attempts += 1;
      window.setTimeout(scrollWhenReady, 50);
    };

    scrollWhenReady();
    return () => {
      cancelled = true;
    };
  }, [location.hash, state.loading]);

  useEffect(() => {
    async function load() {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const query = new URLSearchParams();
      const search = searchParams.get('search');
      const level = searchParams.get('level');
      if (search) query.set('search', search);
      if (level) query.set('level', level);

      try {
        const res = await fetch(`/api/home${query.toString() ? `?${query}` : ''}`);
        if (!res.ok) throw new Error('Failed to load');
        const payload = await res.json();
        setState({ loading: false, error: null, data: payload.data });
      } catch {
        setState({
          loading: false,
          error: 'Unable to load universities. Please try again.',
          data: null
        });
      }
    }

    load();
  }, [searchParams]);

  function applyFilters(level = selectedLevel, search = searchInput, scroll = false) {
    const next = {};
    if (search.trim()) next.search = search.trim();
    if (level) next.level = level;
    setSearchParams(next);
    if (scroll) {
      window.setTimeout(() => scrollToUniversities(), 50);
    }
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    setSuggestOpen(false);
    applyFilters(selectedLevel, searchInput, true);
  }

  function pickSuggestion(item) {
    setSuggestOpen(false);
    if (item.type === 'university' && item.slug) {
      navigate(`/universities/${item.slug}`);
      return;
    }
    setSearchInput(item.query);
    applyFilters(selectedLevel, item.query, true);
  }

  function handleSearchKeyDown(event) {
    if (!suggestOpen || suggestions.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestion((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestion((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (event.key === 'Enter' && activeSuggestion >= 0) {
      event.preventDefault();
      pickSuggestion(suggestions[activeSuggestion]);
    } else if (event.key === 'Escape') {
      setSuggestOpen(false);
    }
  }

  function handleLevelChange(code) {
    setSelectedLevel(code);
    applyFilters(code, searchInput, true);
  }

  function handleProgramNavigate(item) {
    const level = item.level || '';
    const search = item.search || '';
    setSelectedLevel(level);
    setSearchInput(search);
    applyFilters(level, search, true);
  }

  function handleTrending(label) {
    const match = (home.popularCourses || []).find((c) => c.name === label);
    if (match) {
      handleProgramNavigate(match);
      return;
    }
    setSearchInput(label.replace(/^Online\s+/i, ''));
    applyFilters(selectedLevel, label.replace(/^Online\s+/i, ''), true);
  }

  function clearSearch() {
    setSearchInput('');
    applyFilters(selectedLevel, '', false);
  }

  const searchInfo = state.data?.search || null;
  const stats = state.data?.stats;
  const universities = state.data?.universities || [];
  const levels = catalogLevels(state.data?.levels);
  const faqSchema = useMemo(() => buildFaqPageSchema(home.faqs), [home.faqs]);

  return (
    <PageShell onProgramNavigate={handleProgramNavigate}>
      <Seo title={seo.home.title} description={seo.home.description} />
      <JsonLd data={faqSchema} />

      <section className="hero-bleed">
        <div className="container hero-layout">
          <div className="hero-content">
            <p className="brand-kicker">{site.brand}</p>
            <h1>{home.h1}</h1>
            <p className="hero-sub">{home.heroSubheading}</p>
            <form className="hero-search" onSubmit={handleSearchSubmit}>
              <div className="search-box" ref={searchBoxRef}>
                <input
                  className="search-input"
                  placeholder={home.searchPlaceholder}
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setSuggestOpen(true);
                  }}
                  onFocus={() => setSuggestOpen(true)}
                  onKeyDown={handleSearchKeyDown}
                  aria-label={home.searchPlaceholder}
                  role="combobox"
                  aria-expanded={suggestOpen && suggestions.length > 0}
                  aria-controls="search-suggestions"
                  aria-autocomplete="list"
                  autoComplete="off"
                />
                {suggestOpen && suggestions.length > 0 && (
                  <ul className="search-suggestions" id="search-suggestions" role="listbox">
                    {suggestions.map((item, position) => (
                      <li
                        key={`${item.type}-${item.label}`}
                        role="option"
                        aria-selected={position === activeSuggestion}
                        className={`search-suggestion${
                          position === activeSuggestion ? ' active' : ''
                        }`}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          pickSuggestion(item);
                        }}
                      >
                        <span className="search-suggestion-label">{item.label}</span>
                        {item.count ? (
                          <span className="search-suggestion-meta">
                            {item.count} program{item.count === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="cta-button" type="submit">
                {home.searchButton}
              </button>
            </form>
            <p className="hero-quiz-prompt">
              Not sure where to start?{' '}
              <button type="button" className="link-button inline" onClick={openFindCourse}>
                Find my course in 1 minute
              </button>
            </p>
            {stats && (
              <p className="trust-inline">
                {stats.universities_count} universities · {stats.courses_count} programs listed
              </p>
            )}
          </div>
          <div className="hero-lead">
            <LeadForm
              title={site.ctaPrimary}
              subtitle={home.counselSubheading}
              leadSource="home_form"
            />
          </div>
        </div>
      </section>

      <section className="section trending-section">
        <div className="container">
          <p className="trending-label">{home.trendingLabel}</p>
          <div className="trending-row">
            <button type="button" className="trend-chip trend-chip-quiz" onClick={openFindCourse}>
              ✨ Find my course
            </button>
            {(home.trending || []).map((item) => (
              <button
                key={item}
                type="button"
                className="trend-chip"
                onClick={() => handleTrending(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="universities">
        <div className="container">
          <header className="section-header">
            <h2>{home.universitiesHeading}</h2>
            <p>{home.universitiesIntro}</p>
          </header>

          <div className="level-toggle">
            <button
              type="button"
              className={`level-pill ${selectedLevel === '' ? 'active' : ''}`}
              onClick={() => handleLevelChange('')}
            >
              All levels
            </button>
            {levels.map((level) => (
              <button
                key={level.code}
                type="button"
                className={`level-pill ${selectedLevel === level.code ? 'active' : ''}`}
                onClick={() => handleLevelChange(level.code)}
              >
                {level.name}
              </button>
            ))}
          </div>

          {state.loading && <p className="status-text">Loading universities...</p>}
          {state.error && <p className="status-text error">{state.error}</p>}
          {!state.loading && searchInfo && (
            <p className="search-notice" role="status">
              {searchInfo.didYouMean && (
                <>
                  Showing results for <strong>{searchInfo.didYouMean}</strong>.{' '}
                </>
              )}
              {searchInfo.match === 'partial' && (
                <>
                  No exact match for <strong>{searchInfo.term}</strong>. Showing close matches.{' '}
                </>
              )}
              {searchInfo.match === 'none' && (
                <>
                  Nothing found for <strong>{searchInfo.term}</strong>. Try{' '}
                  {(home.trending || []).slice(0, 3).map((label, position, list) => (
                    <React.Fragment key={label}>
                      <button
                        type="button"
                        className="link-button inline"
                        onClick={() => handleTrending(label)}
                      >
                        {label}
                      </button>
                      {position < list.length - 1 ? ', ' : ''}
                    </React.Fragment>
                  ))}{' '}
                  or talk to a counsellor.{' '}
                </>
              )}
              {searchInfo.match !== 'none' && (
                <button type="button" className="link-button inline" onClick={clearSearch}>
                  Clear search
                </button>
              )}
            </p>
          )}

          <div className="college-grid">
            {!state.loading && universities.length === 0 && (
              <article className="college-card">
                <h3>No universities found</h3>
                <p className="college-location">{home.emptyUniversities}</p>
              </article>
            )}
            {universities.map((university) => (
              <Link
                key={university.id}
                to={`/universities/${university.slug}`}
                className={`college-card college-card-link${
                  getCampusImage(university.slug) ? ' has-campus-image' : ''
                }`}
              >
                {getCampusImage(university.slug) && (
                  <div className="college-card-media">
                    <CampusImage slug={university.slug} name={university.name} variant="card" />
                    <CampusCreditText slug={university.slug} className="campus-credit campus-credit-card" />
                  </div>
                )}
                <div className="college-card-header">
                  <div className="college-card-brand">
                    <span className="pill">{university.isOnlineOnly ? 'Online' : 'Online + Hybrid'}</span>
                    <UniversityLogo
                      slug={university.slug}
                      name={university.name}
                      logoUrl={university.logoUrl}
                    />
                  </div>
                  <h3>{university.name}</h3>
                  {locationLabel(university) && (
                    <span className="college-location">{locationLabel(university)}</span>
                  )}
                </div>
                <p className="university-card-meta">
                  {searchInfo && university.matchedCourseCount > 0
                    ? `${university.matchedCourseCount} matching of ${university.courseCount} programs`
                    : `${university.courseCount} program${university.courseCount === 1 ? '' : 's'} available`}
                </p>
                <span className="link-button">View programs →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CounsellorSection stats={stats} />

      <section className="section about-section">
        <div className="container about-panel">
          <h2>{home.aboutHeading}</h2>
          {(home.aboutParagraphs || []).map((paragraph) => (
            <p key={paragraph.slice(0, 32)}>{paragraph}</p>
          ))}
          <Link to="/about" className="link-button">
            About BharatShiksha →
          </Link>
        </div>
      </section>

      <section className="section why-section">
        <div className="container">
          <header className="section-header">
            <h2>{home.whyHeading}</h2>
            <p>{home.whyIntro}</p>
          </header>
          <div className="feature-grid">
            {(home.whyFeatures || []).map((feature) => (
              <article className="feature-card" key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FaqAccordion heading={home.faqHeading} faqs={home.faqs} />

      <section className="section counsel-section">
        <div className="container counsel-layout counsel-layout-cta">
          <div className="counsel-copy">
            <h2>{home.counselHeading}</h2>
            <p>{home.counselSubheading}</p>
            <ul className="counsel-points">
              <li>Free counselling</li>
              <li>UGC-DEB focused guidance</li>
              <li>No spam follow-ups</li>
            </ul>
          </div>
          <a href="#counsel" className="cta-button counsel-jump">
            {site.ctaSubmit}
          </a>
        </div>
      </section>
    </PageShell>
  );
}
